import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, QrCode, UserPlus, Mail, Phone, Loader2, X, Hash } from 'lucide-react';
import { apiClient } from '../../utils/api';
import { QrScannerModal } from './QrScannerModal';
import { useCurrentShop } from '../../contexts/CurrentShopContext';
import { type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../InternationalPhoneInput';
import { DateInput } from '../DateInput';

interface ClientResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate?: string;
  dateOfBirth?: string;
  isMinor?: boolean | null;
}

interface AppointmentPick {
  id: string;
  serviceName?: string;
  scheduledAt: string;
  status: string;
}

interface NewClientData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  isMinor: boolean | null;
}

function isMinorFromBirthDate(birthDate?: string): boolean {
  if (!birthDate) return false;
  const age = (Date.now() - new Date(birthDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  return age < 18;
}

interface ClientArrivalToolbarProps {
  onError?: (message: string) => void;
  onClientAction?: () => void;
}

export function ClientArrivalToolbar({ onError, onClientAction }: ClientArrivalToolbarProps) {
  const navigate = useNavigate();
  const { currentShopId } = useCurrentShop();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ClientResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [focused, setFocused] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [checkInCode, setCheckInCode] = useState('');
  const [resolvingCode, setResolvingCode] = useState(false);
  const [appointmentPicker, setAppointmentPicker] = useState<AppointmentPick[] | null>(null);
  const [pendingClient, setPendingClient] = useState<ClientResult | null>(null);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClientData, setNewClientData] = useState<NewClientData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthDate: '',
    isMinor: null,
  });
  const [newClientPhoneCountry, setNewClientPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [newClientPhoneDisplay, setNewClientPhoneDisplay] = useState('');
  const [newClientPhoneE164, setNewClientPhoneE164] = useState<string | null>(null);
  const [newClientPhoneIsValid, setNewClientPhoneIsValid] = useState(false);

  const handleSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await apiClient(`/api/admin/clients?search=${encodeURIComponent(query)}&limit=8`);
      if (res.ok) {
        const d = await res.json();
        setSearchResults(d.data?.clients ?? d.data ?? []);
      }
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => handleSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const resolveClientDestination = async (client: ClientResult) => {
    const today = new Date().toISOString().split('T')[0];
    const shopFilter = currentShopId ? `&shopId=${encodeURIComponent(currentShopId)}` : '';
    try {
      const res = await apiClient(
        `/api/appointments?clientId=${client.id}&dateFrom=${today}&dateTo=${today}&status=SCHEDULED,CONFIRMED,PENDING_PAYMENT,CHECKED_IN,IN_PROGRESS,AWAITING_CHECKOUT&limit=20${shopFilter}`,
      );
      if (!res.ok) {
        navigate(`/clients/${client.id}`);
        return;
      }
      const d = await res.json();
      const appts: AppointmentPick[] = (d.data?.appointments ?? d.data ?? []).map((a: any) => ({
        id: a.id,
        serviceName: a.serviceName,
        scheduledAt: a.scheduledAt,
        status: a.status,
      }));

      if (appts.length === 1) {
        navigate(`/appointments/${appts[0].id}`);
      } else if (appts.length > 1) {
        setPendingClient(client);
        setAppointmentPicker(appts);
      } else {
        navigate(`/clients/${client.id}`);
      }
      onClientAction?.();
    } catch {
      navigate(`/clients/${client.id}`);
    }
  };

  const handleResolveCheckInCode = async () => {
    const code = checkInCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{3,8}$/.test(code)) {
      onError?.('Saisissez un code d’accueil (3 à 8 caractères, lettres ou chiffres).');
      return;
    }
    setResolvingCode(true);
    try {
      const res = await apiClient(
        `/api/admin/reception/resolve-qr?payload=${encodeURIComponent(code)}`,
      );
      const data = await res.json();
      if (!res.ok || !data.success || !data.data?.appointmentId) {
        throw new Error(data.message || 'Code non reconnu');
      }
      setCheckInCode('');
      navigate(`/appointments/${data.data.appointmentId}`);
      onClientAction?.();
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : 'Code d’accueil non reconnu');
    } finally {
      setResolvingCode(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientData.firstName.trim()) {
      onError?.('Le prénom est requis.');
      return;
    }
    if (newClientPhoneDisplay.trim() && (!newClientPhoneIsValid || !newClientPhoneE164)) {
      onError?.('Le numéro de téléphone est invalide pour le pays sélectionné.');
      return;
    }
    setCreatingClient(true);
    try {
      const res = await apiClient('/api/admin/clients', {
        method: 'POST',
        body: JSON.stringify({
          firstName: newClientData.firstName,
          lastName: newClientData.lastName,
          email: newClientData.email || '',
          phone: newClientPhoneE164 ?? '',
          dateOfBirth: newClientData.birthDate || undefined,
          isMinor: newClientData.isMinor,
          address: {},
          emergencyContact: {},
          preferences: {},
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Erreur de création');
      }
      const d = await res.json();
      const client = d.data?.client ?? d.data;
      setShowNewClient(false);
      setNewClientData({ firstName: '', lastName: '', email: '', phone: '', birthDate: '', isMinor: null });
      setNewClientPhoneCountry('FR');
      setNewClientPhoneDisplay('');
      setNewClientPhoneE164(null);
      setNewClientPhoneIsValid(false);
      await resolveClientDestination(client);
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : 'Erreur lors de la création du client');
    } finally {
      setCreatingClient(false);
    }
  };

  const showDropdown = focused && searchQuery.length >= 2;

  return (
    <>
      <div className="card border border-gray-200/80 shadow-sm p-0 overflow-hidden">
        <div className="bg-gradient-to-r from-primary-50 via-white to-amber-50/40 px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-textPrimary">Accueil rapide</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Recherchez un client, scannez son QR code, saisissez son code d’accueil ou créez une nouvelle fiche.
          </p>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          <div className="lg:col-span-7 relative">
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 block">
              Rechercher un client
            </label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="search"
                className="input w-full pl-12 pr-12 py-3.5 text-base rounded-xl border-gray-200 focus:ring-2 focus:ring-primary-300"
                placeholder="Nom, prénom, email ou téléphone…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => window.setTimeout(() => setFocused(false), 150)}
              />
              {searching && (
                <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-500 animate-spin" />
              )}
            </div>

            {showDropdown && (
              <div className="absolute z-20 left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                {searchResults.length === 0 && !searching ? (
                  <p className="px-4 py-6 text-sm text-gray-500 text-center">Aucun client trouvé</p>
                ) : (
                  <ul className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                    {searchResults.map((client) => {
                      const birth = client.birthDate ?? client.dateOfBirth;
                      const clientIsMinor = birth
                        ? isMinorFromBirthDate(birth)
                        : client.isMinor;
                      return (
                        <li key={client.id}>
                          <button
                            type="button"
                            className="w-full text-left px-4 py-3 hover:bg-primary-50/60 flex items-center gap-3 transition-colors"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setSearchQuery('');
                              setSearchResults([]);
                              setFocused(false);
                              void resolveClientDestination(client);
                            }}
                          >
                            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 text-sm font-bold text-primary-700">
                              {client.firstName?.[0]}{client.lastName?.[0]}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-textPrimary truncate">
                                {client.firstName} {client.lastName}
                              </p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                                {client.email && (
                                  <span className="inline-flex items-center gap-1 truncate">
                                    <Mail className="w-3 h-3" /> {client.email}
                                  </span>
                                )}
                                {client.phone && (
                                  <span className="inline-flex items-center gap-1">
                                    <Phone className="w-3 h-3" /> {client.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                            {clientIsMinor === true && (
                              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                                Mineur
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-5 flex flex-col gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 block">
                Code d’accueil
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    inputMode="text"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={8}
                    className="input w-full pl-9 py-3.5 text-base rounded-xl border-gray-200 tracking-[0.35em] uppercase font-semibold"
                    placeholder="AB12"
                    value={checkInCode}
                    onChange={(e) =>
                      setCheckInCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase())
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleResolveCheckInCode();
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="btn-secondary px-4 py-3.5 rounded-xl disabled:opacity-50"
                  disabled={resolvingCode || checkInCode.length < 3}
                  onClick={() => void handleResolveCheckInCode()}
                >
                  {resolvingCode ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Ouvrir'}
                </button>
              </div>
            </div>
          </div>

            <div className="lg:col-span-12 relative flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                className="btn-primary flex-1 py-3.5 rounded-xl flex items-center justify-center gap-2 text-base"
                onClick={() => setShowQr(true)}
              >
                <QrCode className="w-5 h-5" />
                Scanner QR code
              </button>
              <button
                type="button"
                className="btn-secondary flex-1 py-3.5 rounded-xl flex items-center justify-center gap-2 text-base"
                onClick={() => setShowNewClient(true)}
              >
                <UserPlus className="w-5 h-5" />
                Nouveau client
              </button>
            </div>
        </div>
      </div>

      <QrScannerModal
        isOpen={showQr}
        onClose={() => setShowQr(false)}
        onAppointmentFound={(id) => navigate(`/appointments/${id}`)}
        onError={onError}
      />

      {showNewClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={() => setShowNewClient(false)}>
          <div className="card max-w-lg w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Nouveau client</h3>
              <button type="button" onClick={() => setShowNewClient(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold block mb-1">Prénom *</label>
                <input className="input w-full" value={newClientData.firstName} onChange={(e) => setNewClientData({ ...newClientData, firstName: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold block mb-1">Nom (optionnel)</label>
                <input className="input w-full" value={newClientData.lastName} onChange={(e) => setNewClientData({ ...newClientData, lastName: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold block mb-1">Email</label>
                <input type="email" className="input w-full" value={newClientData.email} onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold block mb-1">Téléphone</label>
                <InternationalPhoneInput
                  value={newClientPhoneDisplay}
                  country={newClientPhoneCountry}
                  onChange={(result) => {
                    setNewClientPhoneDisplay(result.displayValue);
                    setNewClientPhoneCountry(result.country);
                    setNewClientPhoneE164(result.e164Value);
                    setNewClientPhoneIsValid(result.isValid);
                    setNewClientData((prev) => ({ ...prev, phone: result.displayValue }));
                  }}
                  selectClassName="input w-full"
                  inputClassName="input w-full"
                />
                {newClientPhoneDisplay.trim() && !newClientPhoneIsValid && (
                  <p className="mt-1 text-xs text-red-600">
                    Numéro invalide pour le pays sélectionné.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold block mb-1">Date de naissance (optionnel)</label>
                <DateInput
                  className="input w-full"
                  value={newClientData.birthDate}
            disabled={newClientData.isMinor === true && !newClientData.birthDate}
                  onChange={(value: any) =>
                    setNewClientData({
                      ...newClientData,
                      birthDate: value,
                isMinor: value ? isMinorFromBirthDate(value) : null,
                    })
                  }
                />
              </div>
              <label className="flex items-center gap-2 self-end pb-3 text-sm text-textPrimary">
                <input
                  type="checkbox"
            checked={newClientData.isMinor === true}
                  disabled={Boolean(newClientData.birthDate)}
            onChange={(e) => setNewClientData({
              ...newClientData,
              isMinor: e.target.checked ? true : null,
            })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                Mineur (optionnel)
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="button" className="btn-secondary flex-1" onClick={() => setShowNewClient(false)}>Annuler</button>
              <button
                type="button"
                className="btn-primary flex-1 disabled:opacity-50"
                disabled={creatingClient || !newClientData.firstName}
                onClick={() => void handleCreateClient()}
              >
                {creatingClient ? 'Création…' : 'Créer et ouvrir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {appointmentPicker && pendingClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={() => setAppointmentPicker(null)}>
          <div className="card max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-1">Plusieurs rendez-vous aujourd&apos;hui</h3>
            <p className="text-sm text-gray-500 mb-4">
              {pendingClient.firstName} {pendingClient.lastName} — choisissez le rendez-vous à ouvrir.
            </p>
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {appointmentPicker.map((appt) => (
                <li key={appt.id}>
                  <button
                    type="button"
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50 transition-colors"
                    onClick={() => {
                      setAppointmentPicker(null);
                      navigate(`/appointments/${appt.id}`);
                    }}
                  >
                    <p className="font-medium text-sm">{appt.serviceName ?? 'Rendez-vous'}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(appt.scheduledAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      {' · '}
                      {appt.status}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="btn-secondary w-full mt-4"
              onClick={() => {
                setAppointmentPicker(null);
                navigate(`/clients/${pendingClient.id}`);
              }}
            >
              Voir la fiche client
            </button>
          </div>
        </div>
      )}
    </>
  );
}
